import { z } from 'zod';

import { assertNever } from '../../assertNever';
import { BorrowerId } from '../../BrandedIds';
import { PhoneNumber } from '../../Phone';
import { Temporal } from '../../Temporal';
import { EmailString } from '../../validations/email';
import { Field, Schema } from './Schema';

export class Validator {
  public static fromSchema(schema: Schema, enforceRequired = true): z.ZodObject<Record<string, z.ZodType<unknown>>> {
    const validator = z.object(
      Object.fromEntries(
        Object.entries(schema).map(([key, field]) => [key, Validator.fromField(field, enforceRequired)]),
      ),
    );
    return validator;
  }

  private static fromField(field: Field, enforceRequired: boolean): z.ZodType<unknown> {
    const handleRequiredAndMultiValued = (validator: z.ZodType<unknown>): z.ZodType<unknown> => {
      const withMultiValued = field.multiValued ? z.array(validator) : validator;
      if (!enforceRequired) {
        // we want to enforce required fields on step state transitions but not
        // necessarily for draft submissions so based on this flag we may want
        // ALL fields to be optional in some situations
        return withMultiValued.optional();
      }
      const withRequired = field.required ? withMultiValued : withMultiValued.optional();
      return withRequired;
    };

    const getBaseValidator = (): z.ZodType<unknown> => {
      switch (field.schemaType) {
        case 'string': {
          if (field.validValues == null) return z.string();
          const validValueSet = new Set(field.validValues);
          return z.string().refine((value) => validValueSet.has(value!));
        }
        case 'currency':
        case 'number':
          return z
            .number()
            .or(z.string())
            .transform((value, ctx): number => {
              const realValue = typeof value === 'string' ? +value : value;
              if (Number.isNaN(realValue)) {
                ctx.addIssue({ code: 'custom', message: `Invalid currency value: ${value}` });
              }
              if (field.min && realValue < field.min) {
                ctx.addIssue({ code: 'custom', message: `Value must be greater than ${field.min}` });
              }
              if (field.max && realValue > field.max) {
                ctx.addIssue({ code: 'custom', message: `Value must be less than ${field.max}` });
              }
              return realValue;
            });
        case 'boolean':
          return z.boolean().or(
            z.string().transform((value, ctx) => {
              switch (value) {
                case 'true':
                  return true;
                case 'false':
                  return false;
                default:
                  ctx.addIssue({ code: 'custom', message: `Invalid boolean value: ${value}` });
              }
            }),
          );
        case 'plainDate':
          return z
            .instanceof(Temporal.PlainDate)
            .or(z.instanceof(Date))
            .or(z.iso.date()) // ISO-8601 only -- we should probably be more permissive
            .transform((value) =>
              value instanceof Date
                ? Temporal.PlainDate.from(value.toISOString().split('T')[0])
                : Temporal.PlainDate.from(value),
            );
        case 'object':
          return z.object(
            Object.fromEntries(
              Object.entries(field.fields).map(([key, childField]) => [
                key,
                Validator.fromField(childField as Field /* TODO fix this as Field */, enforceRequired),
              ]),
            ),
          );
        case 'email':
          return EmailString;
        case 'phoneNumber':
          return PhoneNumber;
        case 'borrower':
          return BorrowerId;
        default:
          return assertNever(field);
      }
    };

    const wrapped = handleRequiredAndMultiValued(getBaseValidator());

    // Select/enum form elements render a "Select an option" placeholder whose
    // value is the empty string. Treat that as absent so optional selects pass
    // validation (empty string is not a member of validValues); required selects
    // still fail because undefined is rejected by the non-optional validator.
    if (field.schemaType === 'string' && field.validValues != null) {
      return z.preprocess((value) => (value === '' ? undefined : value), wrapped);
    }

    return wrapped;
  }
}
